'use client';

import { useState, useEffect } from 'react';
import CustomCalendar from '@/app/components/CustomCalendar';
import { useLanguage } from '@/contexts/LanguageContext';

const DateRangePicker = ({ 
  checkIn, 
  checkOut, 
  onDateSelect,
  guests = 2,
  children = 0,
  roomCount = 1,
  propertyCode
}) => {
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const { t } = useLanguage();
  
  // Format date for display (e.g., "Wed, Jun 4, 2025")
  const formatDateForDisplay = (dateString) => {
    if (!dateString) return '';
    
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      weekday: 'short', 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  // Handle date selection from calendar
  const handleDateSelect = (startDate, endDate) => {
    if (onDateSelect && startDate && endDate) {
      onDateSelect(startDate, endDate);
    }
    closeCalendar();
  };

  // Handle calendar button click with preventDefault
  const handleCalendarToggle = (e) => {
    e.preventDefault();
    if (isCalendarOpen) {
      closeCalendar();
    } else {
      openCalendar();
    }
  };

  // Open calendar and prevent body scrolling
  const openCalendar = () => {
    // Save current scroll position before doing anything else
    const scrollY = window.scrollY;
    document.body.dataset.scrollY = scrollY;
    
    // Set calendar open state first
    setIsCalendarOpen(true);
    
    // Use setTimeout to add the class after the state update
    setTimeout(() => {
      document.body.classList.add('calendar-open');
      
      // Ensure the page stays at the current scroll position
      window.scrollTo(0, scrollY);
    }, 10);
  };

  // Close calendar and restore body scrolling
  const closeCalendar = () => {
    // Get saved scroll position
    const scrollY = parseInt(document.body.dataset.scrollY || '0');
    
    // Remove the class first
    document.body.classList.remove('calendar-open');
    
    // Then close the calendar
    setIsCalendarOpen(false);
    
    // Restore scroll position after a short delay
    setTimeout(() => {
      window.scrollTo(0, scrollY);
    }, 10);
  };

  // Clean up when component unmounts
  useEffect(() => {
    return () => {
      document.body.classList.remove('calendar-open');
    };
  }, []);

  return (
    <div className="relative">
      {/* Date picker button */}
      <button
        className="flex items-center justify-between w-full px-4 py-2.5 bg-white border border-gray-200 rounded-lg hover:border-gray-300 transition-colors"
        onClick={handleCalendarToggle}
      >
        <div className="flex items-center gap-3">
          <div className="text-left">
            <div className="text-xs text-gray-500 font-medium">Check-in — Check-out</div>
            <div className="font-medium text-base text-gray-800 leading-snug min-w-0">
              {checkIn && checkOut 
                ? `${formatDateForDisplay(checkIn)} — ${formatDateForDisplay(checkOut)}` 
                : t.selectDates}
            </div>
          </div>
        </div>
        <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Calendar Modal */}
      {isCalendarOpen && (
        <CustomCalendar
          onDateSelect={handleDateSelect}
          onClose={closeCalendar}
          guests={guests}
          children={children}
          roomCount={roomCount}
          propertyCode={propertyCode}
          closeNavbar={() => {}}
        />
      )}
    </div>
  );
};

export default DateRangePicker; 